// React Dependencies
import React, { useEffect, useMemo, useRef, useState } from 'react';

// External Dependencies
import moment from 'moment';
import PropTypes from 'prop-types';
import Lightbox from 'react-image-lightbox';
import { toast } from 'react-toastify';
import { ArrowBack, CloudUpload, Visibility } from '@material-ui/icons';
import { Dialog, IconButton, makeStyles, TextareaAutosize, TextField, Tooltip } from '@material-ui/core';

// Internal Dependencies
import MButton from '../../../../../components/MButton';
import MText from '../../../../../components/Common/MText';
import Colours from '../../../../../styles/colours';
import RadioBtnSelect from '../../../../../components/Common/RadioBtnSelect';
import MaterialDatePicker from '../../../../../components/Common/form/MaterialDatePicker';
import { IsEmpty, IsNumber } from '../../../../../common/checks';
import Constants, { IMAGE_FORMATS, StandardVoucherTnC, Status } from '../../../../../common/constants';
import VoucherDesign from './VoucherDesign';
import PhotoCropper from '../../../../../components/Common/photos/PhotoCropper';
import withLoyaltyConnect from '../WithLoyaltyConnect';
import { VoucherImages, VoucherStyles } from '../constants';
import ApiService from '../../../../../services/apiService';

const useStyles = makeStyles({
  helperText: {
    bottom:     -24,
    marginLeft: 0,
    position:   'absolute',
  },
});

const EditVoucher = (props) => {
  const loyaltyCardStatus = props.loyaltyProgram.status;

  const selectedVoucher = props.loyaltyProgram.vouchers.find((vc) => vc.id === props.loyaltyProgram.selectedVoucherId);
  const { status, validityInMonths, issuableQty, minSpend } = selectedVoucher;
  const classes = useStyles();

  const initialValues = useRef(selectedVoucher);

  const [isValidityByDateRange, setIsValidityByDateRange] = useState(IsEmpty(selectedVoucher?.validityInMonths));
  const [isThereMinimumSpend, setIsThereMinimumSpend] = useState(!IsEmpty(selectedVoucher?.minSpend));
  const [isUnlimited, setIsUnlimited] = useState(!IsNumber(selectedVoucher?.issuableQty));
  const [isCropping, setIsCropping] = useState(false);
  const [imgFile, setImgFile] = useState(null);
  const [imgSrc, setImgSrc] = useState(null);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);

  const fileInputRef = useRef(null);

  const validate = (data) => {
    const errs = {};

    if (!isValidityByDateRange && !IsEmpty(data.validityInMonths) && data.validityInMonths <= 0) {
      errs.validityInMonths = 'Minimum 1 month required';
    }

    if (!isUnlimited && !IsEmpty(data.issuableQty) && data.issuableQty <= 0) {
      errs.issuableQty = 'Minimum qty is 1';
    }

    if (isThereMinimumSpend && !IsEmpty(data.minSpend) && data.minSpend <= 0) {
      errs.minSpend = 'Min spend must be more than $0';
    }

    return errs;
  };

  const errors = useMemo(() => validate(selectedVoucher), [validityInMonths, issuableQty, minSpend]);
  const isError = Object.keys(errors).length > 0;

  const isVoucherDeactivated = status === Status.Deactivated || loyaltyCardStatus === Status.Deactivated;
  const isDraftVoucher = status === Status.Draft;

  const updateSelectedVoucherDataOnVouchersList = (data, unselectVoucher = false) => {
    const updatedVouchers = props.loyaltyProgram.vouchers.map((vc) => {
      if (vc.id === selectedVoucher.id) {
        return {
          ...vc,
          ...data,
        };
      } else {
        return vc;
      }
    });

    props.updateLoyaltyCardAction({
      vouchers:          updatedVouchers,
      selectedVoucherId: unselectVoucher ? null : props.loyaltyProgram.selectedVoucherId,
    });
  };

  const onBackToVoucherClick = () => {
    props.updateLoyaltyCardAction({ selectedVoucherId: null });
  };

  const closeCropDialog = () => {
    setIsCropping(false);
    setImgFile(null);
    setImgSrc(null);
  };

  const handleUploadProductImage = async (file) => {
    try {
      props.dispatchStartSavingLC();
      if (IsEmpty(file)) {
        setIsCropping(false);
        return;
      }

      const formData = new FormData();
      formData.append('image', file);

      const imageUrl = await ApiService.uploadGiftProductImage({
        pageId:        props.page.id,
        loyaltyCardId: props.match.params.loyaltyCardId,
        giftId:        props.loyaltyProgram.selectedVoucherId,
      }, formData);

      updateSelectedVoucherDataOnVouchersList({ productImage: imageUrl });
      setIsCropping(false);

      toast.success('Product image uploaded');
    } catch (error) {
      toast.error(error?.message || 'Failed to upload product image');
    } finally {
      props.dispatchFinishSavingLC();
    }
  };

  const handleUpdateVoucher = async () => {
    try {
      props.dispatchStartSavingLC();

      const dataToUpdate = {
        ...selectedVoucher,
        id:               undefined, // remove the value from update data
        balQty:           undefined, // remove the value from update data
        minSpend:         selectedVoucher.minSpend || 0,
        expiryDate:       selectedVoucher.expiryDate ? moment(selectedVoucher.expiryDate).utcOffset('+08').endOf('day').format() : null,
        validityInMonths: selectedVoucher.validityInMonths ? Number(selectedVoucher.validityInMonths) : null,
        issuableQty:      IsEmpty(selectedVoucher.issuableQty) ? null : selectedVoucher.issuableQty,
      };

      const res = await ApiService.updateLoyaltyCardGift({
        pageId:        props.page.id,
        giftId:        selectedVoucher.id,
        loyaltyCardId: props.match.params.loyaltyCardId,
      }, dataToUpdate);

      updateSelectedVoucherDataOnVouchersList({ productImage: res.productImage });

      initialValues.current = {
        ...initialValues.current,
        ...res,
        minSpend:   res.minSpend === 0 ? null : res.minSpend,
        expiryDate: !IsEmpty(res.expiryDate) ? moment(res.expiryDate).utcOffset('+08').format('YYYY-MM-DD') : null,
      };

      toast.success('Voucher updated');
    } catch (error) {
      toast.error(error?.message || 'Failed to update voucher');
    } finally {
      props.dispatchFinishSavingLC();
    }
  };

  const formatDate = (date) => {
    const newDate = new Date(date);
    return moment(newDate).format('YYYY-MM-DD');
  };

  const fileValidated = (file) => {
    if (!IMAGE_FORMATS.includes(file.type)) {
      toast.error('Only JPG, JPEG and PNG files are supported.');
      return false;
    }
    if (file.size > Constants.BinaryOneMBSize * 7) {
      toast.error('Maximum file size of 7MB exceeded.');
      return false;
    }
    return true;
  };

  const handleImageChange = async (e) => {
    const fileInput = e.target;
    if (fileInput.files.length === 0) {
      return;
    }
    const file = fileInput.files[0];
    if (!fileValidated(file)) {
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setImgFile(file);
      setImgSrc(reader.result);
      setIsCropping(true);

      // clear file input
      fileInput.current = '';

      // Clear the e.target.value to ensure that we can select the same image
      e.target.value = null;
    };
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    if (isError) {
      props.disableSaveBtnAction();
    } else {
      props.enableSaveBtnAction();
    }
  }, [isError]);

  useEffect(() => () => {
    props.enableSaveBtnAction();

    updateSelectedVoucherDataOnVouchersList(initialValues.current, true);
  }, []);

  return (
    <div>
      <MButton
        disableElevation
        style={{
          border: '1px solid black',
        }}
        text="Back to vouchers"
        textSize={12}
        type="button"
        className="px-2 mt-3"
        onClick={onBackToVoucherClick}
        icon={() => <ArrowBack />}
      />
      <MText className="mt-3" bold text={`Voucher: ${selectedVoucher?.id}`} />
      <div className="mt-3">
        <MText bold colour={Colours.Gray} text="Voucher Set-Up" />
        <MText colour={Colours.Gray1} text="What will customers receive after they fill up all the stamps on the loyalty card?" />
      </div>

      <div className="mt-3 ml-2">
        <MText text="Voucher Type" bold />
        <MText text="Select the type of voucher" />
        <div className="row mt-1">
          <div className="col-12 col-lg-6">
            <RadioBtnSelect
              options={[{
                label:    'Dollar/Offer Voucher',
                subLabel: 'Please state value (eg. $2 Off, 50% Discount)',
                value:    true,
              }]}
              value={selectedVoucher?.isProduct === false}
              onChange={() => {
                updateSelectedVoucherDataOnVouchersList({ isProduct: false, label: '' });
              }}
              disabled={isVoucherDeactivated}
              labelStyle={{ fontSize: 11 }}
              radioWrapperStyle={{ alignItems: 'flex-start' }}
              radioStyle={{ margin: '0px 9px 9px 9px', padding: 0 }}
            />
            <TextField
              name="label"
              variant="outlined"
              size="small"
              className="mr-2"
              disabled={selectedVoucher?.isProduct !== false || isVoucherDeactivated}
              value={selectedVoucher?.isProduct === false ? selectedVoucher?.label : ''}
              onChange={(e) => {
                updateSelectedVoucherDataOnVouchersList({ label: e.target.value });
              }}
              style={{
                marginLeft: 32,
                width:      'calc(100% - 32px)',
                minWidth:   0,
              }}
            />
          </div>
          <div className="col-12 col-lg-6">
            <RadioBtnSelect
              options={[{
                label:    'Free Item/s Voucher',
                subLabel: 'Please state item/s (eg. Free Coffee)',
                value:    true,
              }]}
              value={selectedVoucher?.isProduct === true}
              onChange={() => {
                updateSelectedVoucherDataOnVouchersList({ isProduct: true, label: '' });
              }}
              disabled={isVoucherDeactivated}
              labelStyle={{ fontSize: 11 }}
              radioWrapperStyle={{ alignItems: 'flex-start' }}
              radioStyle={{ margin: '0px 9px 9px 9px', padding: 0 }}
            />
            <TextField
              name="label"
              variant="outlined"
              size="small"
              className="mr-2"
              disabled={selectedVoucher?.isProduct !== true || isVoucherDeactivated}
              value={selectedVoucher?.isProduct !== false ? selectedVoucher?.label : ''}
              onChange={(e) => {
                updateSelectedVoucherDataOnVouchersList({ label: e.target.value });
              }}
              style={{
                marginLeft: 32,
                width:      'calc(100% - 32px)',
                minWidth:   0,
              }}
            />
            <div className="mt-2" style={{ marginLeft: 32 }}>
              <MText text="Upload picture of free item (optional)" />
              {
                selectedVoucher?.productImage && (
                  <img src={selectedVoucher?.productImage} width={80} height={80} alt="free-item" />
                )
              }
              <div className="d-flex mt-2">
                <div className="mr-2">
                  <input
                    hidden
                    accept={IMAGE_FORMATS}
                    type="file"
                    onChange={handleImageChange}
                    disabled={isVoucherDeactivated}
                    ref={fileInputRef}
                  />
                  <Tooltip title="Upload">
                    <IconButton
                      disabled={selectedVoucher?.isProduct !== true || isVoucherDeactivated}
                      onClick={() => fileInputRef.current.click()}
                      className="shadow-sm bg-white"
                    >
                      <CloudUpload />
                    </IconButton>
                  </Tooltip>
                </div>
                {
                  selectedVoucher?.productImage && (
                    <div className="mr-2">
                      <Tooltip title="View">
                        <IconButton
                          onClick={() => setImageViewerOpen(true)}
                          className="shadow-sm bg-white"
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                    </div>
                  )
                }
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-3 ml-2">
        <MText text="Minimum spend" bold />
        <MText text="Is there any minimum spend required for voucher redemption?" />
        <div className="row mt-2">
          <div className="col-12 col-lg-6">
            <RadioBtnSelect
              options={[{
                label:    'Yes',
                subLabel: 'Please state minimum spend amount (S)',
                value:    true,
              }]}
              value={isThereMinimumSpend}
              onChange={() => setIsThereMinimumSpend(true)}
              disabled={isVoucherDeactivated}
              labelStyle={{ fontSize: 11 }}
              radioWrapperStyle={{ alignItems: 'flex-start' }}
              radioStyle={{ margin: '0px 9px 9px 9px', padding: 0 }}
            />
            <TextField
              name="minSpend"
              variant="outlined"
              size="small"
              type="number"
              className="mr-2"
              disabled={!isThereMinimumSpend || isVoucherDeactivated}
              value={isThereMinimumSpend ? selectedVoucher?.minSpend : ''}
              onChange={(e) => updateSelectedVoucherDataOnVouchersList({ minSpend: e.target.value ? parseInt(e.target.value, 10) : '' })}
              style={{
                marginLeft: 32,
                width:      'calc(100% - 32px)',
                minWidth:   0,
              }}
              inputProps={{
                min: 0,
              }}
              error={Boolean(errors.minSpend)}
              helperText={errors.minSpend}
              FormHelperTextProps={{ className: classes.helperText }}
            />
          </div>
          <div className="col-12 col-lg-6">
            <RadioBtnSelect
              options={[{
                label: 'No minimum spend',
                value: true,
              }]}
              value={!isThereMinimumSpend}
              onChange={() => {
                updateSelectedVoucherDataOnVouchersList({ minSpend: null });
                setIsThereMinimumSpend(false);
              }}
              disabled={isVoucherDeactivated}
              radioWrapperStyle={{ alignItems: 'flex-start' }}
              radioStyle={{ margin: '0 9px 9px 9px', padding: 0 }}
              labelStyle={{ position: 'relative', top: '-4px', fontSize: 11   }}
            />
          </div>
        </div>
      </div>

      <div className="mt-4 mb-1 ml-2">
        <MText text="Voucher Image" bold />
        <MText text="Select the colour for the voucher" />
        <div className="d-flex flex-row flex-wrap" style={{ gap: 12 }}>
          {
            Object.values(VoucherImages).map((vi) => (
              <VoucherDesign
                key={vi}
                src={vi}
                disabled={isVoucherDeactivated}
                selectedVoucherDesign={selectedVoucher?.design}
                pageLogo={props.page.pageLogo}
                colour={VoucherStyles[vi].fontColor}
                onClick={() => {
                  if (!isVoucherDeactivated) {
                    updateSelectedVoucherDataOnVouchersList({ design: vi });
                  }
                }}
              />
            ))
          }
        </div>
      </div>

      <div className="mt-4 mb-2 ml-2">
        <MText text="Voucher Validity" bold />
        <div className="mt-2 ml-2">
          <RadioBtnSelect
            options={[{
              label:    'Fixed Expiry Date',
              subLabel: 'Select Expiry Date',
              value:    true,
            }]}
            value={isValidityByDateRange}
            onChange={() => {
              setIsValidityByDateRange(true);
              updateSelectedVoucherDataOnVouchersList({ validityInMonths: '' });
            }}
            disabled={isVoucherDeactivated || !isDraftVoucher}
            labelStyle={{ fontWeight: 400, fontSize: 11 }}
            radioWrapperStyle={{ alignItems: 'flex-start' }}
            radioStyle={{ margin: '0px 9px 9px 0px', padding: 0 }}
          />
          <MaterialDatePicker
            minDate={false}
            variant="outlined"
            value={selectedVoucher?.expiryDate}
            disablePast
            disabled={!isValidityByDateRange || isVoucherDeactivated || !isDraftVoucher}
            onChange={(e) => updateSelectedVoucherDataOnVouchersList({
              expiryDate: e ? formatDate(e.toString('yyyy-MM-dd')) : null,
            })}
            className="mt-0"
            style={{
              marginLeft: 21,
            }}
          />
        </div>
        <div className="mt-2 ml-2">
          <RadioBtnSelect
            options={[{
              label:    'Fixed Expiry Period',
              subLabel: 'Fill in the number of months the voucher is valid for, after it is claimed',
              value:    true,
            }]}
            value={!isValidityByDateRange}
            onChange={() => {
              setIsValidityByDateRange(false);
              updateSelectedVoucherDataOnVouchersList({ expiryDate: null });
            }}
            disabled={isVoucherDeactivated || !isDraftVoucher}
            labelStyle={{ fontWeight: 400, fontSize: 11 }}
            radioWrapperStyle={{ alignItems: 'flex-start' }}
            radioStyle={{ margin: '0px 9px 9px 0px', padding: 0 }}
          />
          <div className="d-flex flex-row align-items-center">
            <TextField
              name="validityInMonths"
              variant="outlined"
              size="small"
              type="number"
              className="mr-2"
              disabled={isValidityByDateRange || isVoucherDeactivated || !isDraftVoucher}
              value={selectedVoucher?.validityInMonths}
              onChange={(e) => updateSelectedVoucherDataOnVouchersList({ validityInMonths: e.target.value ? parseInt(e.target.value, 10) : '' })}
              style={{
                marginLeft: 21,
                width:      237,
              }}
              inputProps={{
                min:       1,
                className: 'text-center',
              }}
              error={Boolean(errors.validityInMonths)}
              helperText={errors.validityInMonths}
              FormHelperTextProps={{ className: classes.helperText }}
            />
            <MText text="months" size={11} colour={Colours.Gray} />
          </div>
        </div>
      </div>

      <div className="mt-4 ml-2">
        <MText text="Voucher Terms & Conditions" bold />
        <div className="mt-2" style={{ marginLeft: 28 }}>
          <MText text="Standard T&Cs" />
          <MText className="ml-2" style={{ lineHeight: 1.2 }} text={StandardVoucherTnC} />
          <div className="mt-2">
            <MText text="Additional T&Cs (Please amend and add as necessary)" />
            <TextareaAutosize
              className="w-100 p-2"
              minRows={5}
              maxRows={5}
              disabled={isVoucherDeactivated}
              value={selectedVoucher?.terms}
              onChange={(e) => updateSelectedVoucherDataOnVouchersList({ terms: e.target.value })}
              style={{
                resize:       'none',
                background:   'transparent',
                border:       `1px solid ${Colours.Gray5}`,
                fontSize:     11,
                borderRadius: 10,
                lineHeight:   1.2,
              }}
            />
          </div>
        </div>
      </div>

      <div className="mt-4 ml-2 mb-2">
        <MText text="Voucher Quantity" bold />
        <div className="row">
          <div className="col-3 col-md-12 col-lg-3">
            <RadioBtnSelect
              options={[{
                label: 'Unlimited',
                value: true,
              }]}
              value={isUnlimited}
              labelStyle={{ fontSize: 11 }}
              onChange={() => {
                setIsUnlimited(true);
                updateSelectedVoucherDataOnVouchersList({ issuableQty: '' });
              }}
              disabled={isVoucherDeactivated}
              radioWrapperStyle={{ alignItems: 'center' }}
              radioWrapperClassName="mb-0"
              radioStyle={{ margin: 9, padding: 0 }}
            />
          </div>
          <div className="col-9 col-md-12 col-xl-5 d-flex flex-row">
            <RadioBtnSelect
              options={[{
                label: 'Fixed',
                value: true,
              }]}
              value={!isUnlimited}
              labelStyle={{ fontSize: 11 }}
              onChange={() => {
                setIsUnlimited(false);
              }}
              disabled={isVoucherDeactivated}
              radioWrapperClassName="mr-3 mb-0"
              radioWrapperStyle={{ alignItems: 'center' }}
              radioStyle={{ margin: 9, padding: 0 }}
            />
            <TextField
              name="issuableQty"
              variant="outlined"
              size="small"
              type="number"
              disabled={isUnlimited || isVoucherDeactivated}
              value={selectedVoucher?.issuableQty}
              onChange={(e) => {
                const qty = e.target.value ? parseInt(e.target.value, 10) : '';
                updateSelectedVoucherDataOnVouchersList({
                  issuableQty: qty,
                  balQty:      qty,
                });
              }}
              inputProps={{
                min:       1,
                className: 'text-center',
              }}
              error={Boolean(errors.issuableQty)}
              helperText={errors.issuableQty}
              FormHelperTextProps={{ className: classes.helperText }}
            />
          </div>

        </div>
        <div className="d-flex justify-content-center align-items-center mt-3">
          <div className="d-flex flex-column align-items-center mt-4" style={{ maxWidth: 170 }}>
            <MButton
              text="Update"
              textStyle={{ fontWeight: 600 }}
              style={{
                width:    '100%',
                maxWidth: 120,
                height:   42,
              }}
              disableElevation
              colour={Colours.Secondary}
              textColour={Colours.White}
              onClick={() => handleUpdateVoucher()}
            />
            <MText
              className="text-center mt-2"
              text="Save changes made to current voucher"
              size={11}
            />
          </div>
        </div>
      </div>

      <Dialog
        fullScreen
        open={isCropping}
        onClose={closeCropDialog}
      >
        <PhotoCropper
          width={4}
          height={3}
          cropPixelWidth={400}
          cropPixelHeight={400}
          outputPixelWidth={900}
          outputPixelHeight={900}
          type="upload"
          src={imgSrc}
          file={imgFile}
          handleSave={handleUploadProductImage}
          handleClose={closeCropDialog}
          isCropping={isCropping}
        />
      </Dialog>
      {
        imageViewerOpen && !IsEmpty(selectedVoucher?.productImage) && (
          <Lightbox
            animationDisabled
            mainSrc={selectedVoucher?.productImage}
            onCloseRequest={() => setImageViewerOpen(false)}
          />
        )
      }
    </div>
  );
};

EditVoucher.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      loyaltyCardId: PropTypes.number,
    }),
  }).isRequired,
  loyaltyProgram: PropTypes.shape({
    vouchers: PropTypes.arrayOf(PropTypes.shape({
      id:               PropTypes.string.isRequired,
      isProduct:        PropTypes.bool,
      terms:            PropTypes.string,
      validityInMonths: PropTypes.number,
      expiryDate:       PropTypes.string,
      minSpend:         PropTypes.number,
      issuableQty:      PropTypes.number,
      design:           PropTypes.string,
      productImage:     PropTypes.string,
      balQty:           PropTypes.number,
    })).isRequired,
    selectedVoucherId: PropTypes.string,
    status:            PropTypes.oneOf([Status.Draft, Status.Activated, Status.Deactivated]),
  }).isRequired,
  page: PropTypes.shape({
    id:       PropTypes.number,
    pageLogo: PropTypes.string,
  }).isRequired,
  updateLoyaltyCardAction: PropTypes.func.isRequired,
  disableSaveBtnAction:    PropTypes.func.isRequired,
  enableSaveBtnAction:     PropTypes.func.isRequired,
  dispatchStartSavingLC:   PropTypes.func.isRequired,
  dispatchFinishSavingLC:  PropTypes.func.isRequired,
};

export default withLoyaltyConnect(EditVoucher);
